import mainService from "services/mainService";
import { faceScanMiddleware } from "utils/faceScanUtils";

/**
 * --------------------------------------------------
 * @param {number} pkBigcompany
 * @param {string} inputUnits
 * @example
 * listUnits({ inputUnits: "unidade", pkBigcompany: "2" })
 */
export function listUnits({ pkBigcompany, inputUnits = "" }) {
  return mainService.get(
    `v2/bigcompany/${pkBigcompany}/units/names_2?establishment_address=true&establishment_active=true&name=${inputUnits}`
  );
}

/**
 * --------------------------------------------------
 * @param {number} offerId
 * @example
 * duplicateCancelledJob({ offerId: 124 })
 */
export function duplicateCancelledJob({ offerId }) {
  return mainService.post(`v2/jobs/offer/${offerId}/duplicate_cancelled_job`);
}

/**
 * --------------------------------------------------
 * @param {number} offerId
 * @example
 * getOfferDetails({ offerId: 124 })
 */
export function getOfferDetails({ offerId }) {
  return mainService.get(`v2/jobs/offer/${offerId}/establishment_v2`);
}

/**
 * --------------------------------------------------
 * @param {number} jobId
 * @example
 * getJobDetails({ jobId: 124 })
 */
export function getJobDetails({ jobId }) {
  return mainService.get(`v2/jobs/establishment/job/${jobId}/detail`);
}

/**
 * --------------------------------------------------
 * @param {number} offerId
 * @example
 * getWorkerByOffer({ offerId: 124 })
 */
export function getWorkerByOffer({ offerId }) {
  return mainService.get(`v2/jobs/offer/${offerId}/establishment/worker`);
}

/**
 * --------------------------------------------------
 * @param {number} jobId
 * @example
 * getApprovalDetails({ jobId: 124 })
 */
export function getApprovalDetails({ jobId }) {
  return mainService.get(`v2/jobs/approvals/jobs/${jobId}/detail`);
}

/**
 * --------------------------------------------------
 * @param {number} offerId
 * @example
 * getOfferExtraValues({ offerId: 124 })
 */
export function getOfferExtraValues({ offerId }) {
  return mainService.get(`v2/jobs/offer/${offerId}/extra_money/establishment`);
}

/**
 * --------------------------------------------------
 * @param {number} offerId
 * @example
 * revertJob({ offerId: 124 })
 */
export function revertJob({ offerId }) {
  return mainService.post(`v2/jobs/offer/${offerId}/revert_job_started`);
}

/**
 * --------------------------------------------------
 * @param {number} offerId
 * @example
 * rateJob({ offerId: 124 })
 */
export const rateJob = ({ body, offerId }) => {
  return mainService.post(`v2/jobs/offer/${offerId}/rate`, body);
};

/**
 * --------------------------------------------------
 * @param {number} estabPk - ID do estabelecimento
 * @param {number} workerPk - ID do trabalhador
 * @example
 * cancelAllJobs({ estabPk: 123, workerPk: 456 })
 */
export const cancelAllJobs = ({ estabPk, workerPk }) => {
  return mainService.post(`v2/jobs/establishment/${estabPk}/cancel_all_jobs`, {
    worker: workerPk,
  });
};

/**
 * --------------------------------------------------
 * @param {number} offerId
 * @param {date} endTime
 * @example
 * getProportionalOfferValue({ offerId: 124, endTime: "2025-06-10T13:46:39.626" })
 */
export const getProportionalOfferValue = ({ offerId, endTime }) => {
  return mainService.post(`v2/jobs/offer/${offerId}/get_proportional_value`, {
    end_time: new Date(endTime).toISOString(),
  });
};

/**
 * --------------------------------------------------
 * @param {number} estabPk - ID do estabelecimento
 * @param {number} workerPk - ID do trabalhador
 * @example
 * cancelAllJobsAndDuplicate({ estabPk: 123, workerPk: 456 })
 */
export const cancelAllJobsAndDuplicate = ({ estabPk, workerPk }) => {
  return mainService.post(
    `v2/jobs/establishment/${estabPk}/worker/${workerPk}/cancel_all_and_duplicate_offers`
  );
};

/**
 * --------------------------------------------------
 * @param {number} establishmentId
 * @example
 * getEstablishmentJobSettings({ establishmentId: 468 })
 */
export const getEstablishmentJobSettings = ({ establishmentId }) => {
  return mainService.get(`api/establishment/${establishmentId}/job_settings`);
};

/**
 * --------------------------------------------------
 * @comment Parâmetros convencionais para inicialização do job
 * @param {number} offerId
 * --------------------------------------------------
 * @comment Atributos para inicialização do job utilizando face scan
 * @param {object?} faceScan
 * @param {binary} faceScan.image
 * @param {object} faceScan.location
 * @param {number} faceScan.location.latitude
 * @param {number} faceScan.location.longitude
 * @example
 * startJob({ offerId: 468, faceScan: { image: null, location: null } })
 */

export function startJob({ offerId, faceScan }) {
  const { body, headers } = faceScanMiddleware({ offerId, faceScan });

  return mainService.post(`v2/jobs/offer/${offerId}/start`, body, {
    headers,
  });
}

/**
 * --------------------------------------------------
 * @comment Parâmetros convencionais para finalização do job
 * @param {number} offerId
 * --------------------------------------------------
 * @comment Atributos para finalização do job utilizando face scan
 * @param {object?} faceScan
 * @param {binary} faceScan.image
 * @param {object} faceScan.location
 * @param {number} faceScan.location.latitude
 * @param {number} faceScan.location.longitude
 * @example
 * finishJob({ offerId: 468, faceScan: { image: null, location: null } })
 */
export function finishJob({ offerId, faceScan }) {
  const { body, headers } = faceScanMiddleware({ offerId, faceScan });

  return mainService.post(`v2/jobs/offer/${offerId}/finish`, body, {
    headers,
  });
}

/**
 * --------------------------------------------------
 * @comment Parâmetros convencionais para finalização forçada do job
 * @param {number} offerId
 * @param {object} extraParams
 * @param {boolean} extraParams.pay_proportionately
 * @param {string} extraParams.reason
 * @param {string?} extraParams.end_time
 * @param {number?} extraParams.value
 * --------------------------------------------------
 * @comment Atributos para finalização forçada do job utilizando face scan
 * @param {object?} faceScan
 * @param {binary} faceScan.image
 * @param {object} faceScan.location
 * @param {number} faceScan.location.latitude
 * @param {number} faceScan.location.longitude
 * @example
 * finishJob({ offerId: 468, faceScan: { image: null, location: null } })
 */
export function forceFinishJob({ offerId, extraParams, faceScan }) {
  const { body, headers } = faceScanMiddleware({
    offerId,
    faceScan,
    extraParams,
  });

  return mainService.post(
    `v2/jobs/offer/${offerId}/force_finish_by_establishment`,
    body,
    {
      headers,
    }
  );
}
